import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchOrdersByType,
  ORDER_LIST_REDUX_SCOPE
} from '@/redux/order/maternityCenterOrderSlice';
import { formatDayString, renderPhoneStr, renderTextStr } from '@/utils/renderUtils';

const TAB_CHECK_IN = {
  全部订单: '',
  待服务: '1',
  服务中: '2',
  已完成: '3'
};

const tabs = ['全部订单', '待服务', '服务中', '已完成'];

const statusColors = {
  待服务: '#fbbf24',
  服务中: '#00e5ff',
  已完成: '#00d4aa',
  待入住: '#fbbf24',
  温馨入住中: '#00e5ff',
  已顺利离院: '#00d4aa'
};

const maskName = (name) => {
  const s = name == null ? '' : String(name).trim();
  if (!s) return '--';
  return s[0] + '*'.repeat(Math.max(s.length - 1, 1));
};

function OrderList({ storeId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orderListPage = useSelector((state) => state.order?.order?.orderListPage);
  const { data, query } = orderListPage || {};
  const [activeTab, setActiveTab] = useState('全部订单');
  const scrollRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';
  const tabCheckIn = TAB_CHECK_IN[activeTab] || '';

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    const listQuery = {
      storeId: selectedStoreId,
      pageNumber: 1,
      pageSize: 50
    };
    if (tabCheckIn) listQuery.checkInStatus = tabCheckIn;
    const pull = () => {
      dispatch(fetchOrdersByType({
        query: listQuery,
        orderType: 'maternity_center',
        orderListScope: ORDER_LIST_REDUX_SCOPE.ORDER_LIST_PAGE,
        silent: true
      })).unwrap().catch(() => {});
    };
    pull();
    const timer = setInterval(pull, 30000);
    return () => clearInterval(timer);
  }, [dispatch, selectedStoreId, tabCheckIn]);

  const records = useMemo(() => {
    if (String(query?.storeId ?? '') !== selectedStoreId) return [];
    if (String(query?.checkInStatus ?? '') !== tabCheckIn) return [];
    return Array.isArray(data?.records) ? data.records : [];
  }, [data, query, selectedStoreId, tabCheckIn]);

  const selectedOrder = records.find((order) => String(order.id) === selectedId) || null;

  useEffect(() => {
    posRef.current = 0;
    if (scrollRef.current) {
      scrollRef.current.style.transform = 'translateY(0px)';
    }
  }, [records, activeTab]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const halfHeight = el.scrollHeight / 2;
    const speed = 0.3;
    const animate = () => {
      if (!paused && !selectedId && halfHeight > 0) {
        posRef.current += speed;
        if (posRef.current >= halfHeight) {
          posRef.current -= halfHeight;
        }
        el.style.transform = `translateY(-${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused, selectedId, records]);

  const renderRow = (order, index) => {
    const startTimeRaw = (order.momCheckInTime && order.momCheckInTime !== 0)
      ? order.momCheckInTime
      : order.momAppointInTime;
    const amountRaw = order.orderAmount;
    const amountN = Number(amountRaw);
    let amountText = '--';
    if (amountRaw != null && amountRaw !== '' && Number.isFinite(amountN)) {
      let seenFirstDigit = false;
      const masked = String(amountN).replace(/\d/g, (d) => {
        if (seenFirstDigit) return '*';
        seenFirstDigit = true;
        return d;
      });
      amountText = `¥${masked}`;
    }
    const color = statusColors[order.orderStatus] || statusColors[activeTab] || '#00e5ff';
    return (
      <div key={`${order.id}-${index}`} className="ol-row" onClick={() => setSelectedId(String(order.id))}>
        <span className="ol-col ol-col-id">{renderTextStr(order.id)}</span>
        <span className="ol-col ol-col-name">{maskName(order.customerName)}</span>
        <span className="ol-col ol-col-phone">{renderPhoneStr(order.customerPhone)}</span>
        <span className="ol-col ol-col-service">
          <span className="ol-time-start">{formatDayString(startTimeRaw) || '--'}</span>
          <span className="ol-time-sep">~</span>
          <span className="ol-time-end">{formatDayString(order.leaveTime) || '--'}</span>
        </span>
        <span className="ol-col ol-col-store">{renderTextStr(order.storeName)}</span>
        <span className="ol-col ol-col-package">{renderTextStr(order.packageName)}</span>
        <span className="ol-col ol-col-amount">
          <span className="ol-amount" style={{ color }}>{amountText}</span>
        </span>
      </div>
    );
  };

  return (
    <div className="order-list-panel">
      <div className="ol-header">
        <div className="ol-tabs">
          {tabs.map((tab) => (
            <span
              key={tab}
              className={`ol-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </span>
          ))}
        </div>
        <span
          className="ol-view-more"
          onClick={() => {
            if (!selectedStoreId) return;
            navigate(`/client/order/orderList?storeId=${selectedStoreId}`);
          }}
        >
          查看更多 ›
        </span>
      </div>

      <div className="ol-table-header">
        <span className="ol-col ol-col-id">订单号</span>
        <span className="ol-col ol-col-name">姓名</span>
        <span className="ol-col ol-col-phone">电话</span>
        <span className="ol-col ol-col-service">服务时间</span>
        <span className="ol-col ol-col-store">门店</span>
        <span className="ol-col ol-col-package">套餐</span>
        <span className="ol-col ol-col-amount">金额</span>
      </div>

      <div
        className="ol-scroll-container"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="ol-scroll-content" ref={scrollRef}>
          {records.map(renderRow)}
          {records.length > 0 ? records.map((order, index) => renderRow(order, `dup-${index}`)) : null}
        </div>
      </div>
      {selectedOrder && createPortal(
        <div className="ol-modal-overlay" onClick={() => setSelectedId('')}>
          <div className="ol-modal ol-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="ol-side">
              <div className="ol-side-title">订单列表</div>
              <div className="ol-side-wrap">
                <div className="ol-side-scroll">
                  {records.map((order) => {
                    const startRaw = (order.momCheckInTime && order.momCheckInTime !== 0)
                      ? order.momCheckInTime
                      : order.momAppointInTime;
                    return (
                      <div
                        key={order.id}
                        className={`ol-side-item${String(order.id) === selectedId ? ' active' : ''}`}
                        onClick={() => setSelectedId(String(order.id))}
                      >
                        <span className="ol-side-id">{`#${order.id}`}</span>
                        <span className="ol-side-name">{maskName(order.customerName)}</span>
                        <span className="ol-side-phone">{renderPhoneStr(order.customerPhone)}</span>
                        <span className="ol-side-range">{`${formatDayString(startRaw) || '--'}~${formatDayString(order.leaveTime) || '--'}`}</span>
                        <span className="ol-side-room">{renderTextStr(order.storeName)}</span>
                        <span className="ol-side-package">{renderTextStr(order.packageName)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="ol-main">
              <div className="ol-modal-header">
                <span className="ol-modal-title">
                  <span className="ol-modal-id">{`#${selectedOrder.id}`}</span>
                  <span className={`ol-modal-status ol-modal-status-${selectedOrder.orderStatus || ''}`}>
                    {renderTextStr(selectedOrder.orderStatus)}
                  </span>
                </span>
                <button type="button" className="ol-modal-close" onClick={() => setSelectedId('')} title="关闭">×</button>
              </div>
              <div className="ol-modal-body">
                <table className="ol-modal-table">
                  <tbody>
                    <tr>
                      <td className="ol-modal-label">客户姓名</td>
                      <td className="ol-modal-value">{maskName(selectedOrder.customerName)}</td>
                      <td className="ol-modal-label">联系电话</td>
                      <td className="ol-modal-value mono">{renderPhoneStr(selectedOrder.customerPhone)}</td>
                    </tr>
                    <tr>
                      <td className="ol-modal-label">服务门店</td>
                      <td className="ol-modal-value">{renderTextStr(selectedOrder.storeName)}</td>
                      <td className="ol-modal-label">套餐方案</td>
                      <td className="ol-modal-value">{renderTextStr(selectedOrder.packageName)}</td>
                    </tr>
                    <tr>
                      <td className="ol-modal-label">服务时间</td>
                      <td className="ol-modal-value" colSpan={3}>
                        <span className="ol-modal-time">
                          {formatDayString((selectedOrder.momCheckInTime && selectedOrder.momCheckInTime !== 0) ? selectedOrder.momCheckInTime : selectedOrder.momAppointInTime) || '--'}
                        </span>
                        <span className="ol-modal-time-sep">~</span>
                        <span className="ol-modal-time">{formatDayString(selectedOrder.leaveTime) || '--'}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default OrderList;
